import { EyeIcon, MapPinIcon, UserCircleIcon } from "@heroicons/react/24/outline"
import { clsx } from "clsx"

import type { PresentationViewDto } from "~/api/dto/presentation-share.dto"

import {
  ENGAGED_LABEL,
  formatTimeAgo,
  formatViewDuration,
  GLANCED_LABEL,
  isEngaged,
  NO_VIEWS_LABEL,
} from "./activity-display"

export const ACTIVITY_HEADING = `Activity`
export const ACTIVITY_LOADING_LABEL = `Loading activity`
export const ACTIVITY_ERROR_PROMPT = `Unable to load activity`

/**
 * A Share link's Activity, in the proposal pages' tokens: one row per recorded
 * visit with its relative time, viewing duration, Engaged/Glanced
 * badge and approximate location. No totals, averages or unique counts.
 */
export function ActivityList({
  views,
  isLoading,
  isError,
}: {
  views: PresentationViewDto[]
  isLoading: boolean
  isError: boolean
}): JSX.Element {
  return (
    <section
      aria-label={ACTIVITY_HEADING}
      className="rounded-lg border border-bp-border bg-bp-elevated"
    >
      <h3 className="flex items-center gap-2 border-b border-bp-border px-4 py-3 text-[length:var(--step--1)] font-semibold text-bp-text">
        <EyeIcon className="size-4 text-bp-accent" />
        {ACTIVITY_HEADING}
      </h3>
      {isLoading ? (
        <p
          role="status"
          className="px-4 py-4 text-[length:var(--step--2)] text-bp-text-muted"
        >
          {ACTIVITY_LOADING_LABEL}
        </p>
      ) : isError ? (
        <p
          role="alert"
          className="px-4 py-4 text-[length:var(--step--2)] text-bp-danger"
        >
          {ACTIVITY_ERROR_PROMPT}
        </p>
      ) : views.length === 0 ? (
        <p className="px-4 py-4 text-[length:var(--step--2)] text-bp-text-muted">
          {NO_VIEWS_LABEL}
        </p>
      ) : (
        <ul className="divide-y divide-bp-border">
          {views.map((view) => {
            const engaged = isEngaged(view.viewDuration)
            return (
              <li key={view.id} className="flex gap-3 px-4 py-3">
                <UserCircleIcon className="mt-0.5 size-6 shrink-0 text-bp-text-dim" />
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                    <span className="min-w-0 text-[length:var(--step--2)] text-bp-text-muted">
                      <span className="font-semibold text-bp-text">
                        {formatTimeAgo(view.createdAt)}
                      </span>
                      {` · ${formatViewDuration(view.viewDuration)} viewing time`}
                    </span>
                    <span
                      className={clsx(
                        `inline-flex w-fit items-center rounded-full border px-2.5 py-0.5 text-[length:var(--step--2)] font-medium`,
                        engaged
                          ? `border-bp-success/40 bg-bp-success-soft text-bp-success`
                          : `border-bp-border-strong bg-bp-elevated-2 text-bp-text-muted`,
                      )}
                    >
                      {engaged ? ENGAGED_LABEL : GLANCED_LABEL}
                    </span>
                  </div>
                  {view.location ? (
                    <span className="flex min-w-0 items-center gap-1 text-[length:var(--step--2)] text-bp-text-dim">
                      <MapPinIcon className="size-3 shrink-0" />
                      {/* Location arrives from an unauthenticated public
                          tracker; it is shown, never allowed to set the
                          drawer's width. */}
                      <span className="truncate" title={view.location}>
                        {view.location}
                      </span>
                    </span>
                  ) : null}
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
